import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable, Functions } from 'firebase/functions';

import { AppThunk } from '../../../index';
import { signUpFailed, signUpStarted, signUpSuccess } from '../reducer';
import { logInUser } from './logInUser';
import { COLLECTION } from '../../../../firestore/collectionNames';
import {
  RegistrationTrackingData,
  UserType,
} from '../../../../models/documents/UserDocument';

export interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  termsAndPrivacyPolicy: boolean;
  lang: string;
  timezone: string;
  ip: string | null;
  phoneNumber: string | null;
  referralCode?: string;
  userType: UserType;
  registrationTracking: RegistrationTrackingData;
}

async function getCountry(functions: Functions): Promise<string | null> {
  try {
    const getUserMetadata = httpsCallable(functions, 'users-getUserMetadata');
    const result = await getUserMetadata();

    return (result.data as { body: { country?: string } }).body.country || null;
  } catch (_e) {
    return null;
  }
}

export const signUpUser =
  (
    {
      email,
      password,
      lastName,
      firstName,
      termsAndPrivacyPolicy,
      lang,
      timezone,
      ip,
      phoneNumber,
      referralCode,
      userType,
      registrationTracking,
    }: RegisterData,
    callback?: () => void,
    onFail?: () => void
  ): AppThunk =>
  async (dispatch, _, { auth, functions, db }) => {
    dispatch(signUpStarted());
    try {
      const res = await createUserWithEmailAndPassword(auth(), email, password);
      if (!res.user) {
        throw new Error('Error when creating new account');
      }

      const country = await getCountry(functions);

      const userRef = doc(db, COLLECTION.USERS, res.user.uid);
      await setDoc(userRef, {
        firstName,
        lastName,
        email: email.toLowerCase(),
        ip,
        uid: res.user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        termsAndPolicyAcceptDate: serverTimestamp(),
        termsAndPrivacyPolicy,
        lang,
        timezone,
        ...(country && { country }),
        phoneNumber,
        ...(referralCode && { referredBy: referralCode }),
        userType,
        registrationTracking,
      });
      // The verification email is sent server-side by the users/{userId}
      // onCreate trigger, so it still goes out if the browser closes here.
      dispatch(signUpSuccess());

      dispatch(logInUser({ email, password }));
      if (callback) {
        callback();
      }
    } catch (e) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const error = e as any;
      dispatch(signUpFailed(error.code));

      if (onFail) {
        onFail();
      }
    }
  };
